import React, { useState } from 'react'
import { gql } from 'graphql-request'
import { authorizedGraphQLRequest } from '@/api/graphqlClient'
import { AlertTriangle, Power, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { OrganisationWithDetails } from '../index'

interface DangerTabProps {
  organisation: OrganisationWithDetails | null
  loading: boolean
  onRefresh: () => void
  onOrganisationDeleted: () => void
}

const DEACTIVATE_ORGANISATION = gql`
  mutation deactivateOrganisation($organisationId: Int!, $isActive: Boolean!) {
    deactivateOrganisation(organisationId: $organisationId, isActive: $isActive) {
      id
      name
      isActive
    }
  }
`

const DELETE_ORGANISATION = gql`
  mutation deleteOrganisation($organisationId: Int!) {
    deleteOrganisation(organisationId: $organisationId) {
      success
      message
    }
  }
`

const DangerTab: React.FC<DangerTabProps> = ({ organisation, loading, onRefresh, onOrganisationDeleted }) => {
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleDeactivate = async () => {
    if (!organisation) return

    const action = organisation.isActive ? 'deactivate' : 'activate'
    if (!confirm(`Are you sure you want to ${action} "${organisation.name}"?`)) return

    try {
      setSubmitting(true)
      setError(null)
      await authorizedGraphQLRequest(DEACTIVATE_ORGANISATION, {
        organisationId: organisation.id,
        isActive: !organisation.isActive,
      })
      onRefresh()
    } catch (err: any) {
      console.error('Failed to update organisation status:', err)
      const message = err?.response?.errors?.[0]?.message || 'Failed to update organisation status'
      setError(message)
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (!organisation) return

    if (!confirm(`Are you sure you want to DELETE "${organisation.name}"? This action cannot be undone.`)) return
    if (!confirm(`Please confirm again: DELETE "${organisation.name}" permanently?`)) return

    try {
      setSubmitting(true)
      setError(null)
      await authorizedGraphQLRequest(DELETE_ORGANISATION, {
        organisationId: organisation.id,
      })
      onOrganisationDeleted()
    } catch (err: any) {
      console.error('Failed to delete organisation:', err)
      const message = err?.response?.errors?.[0]?.message || 'Failed to delete organisation'
      setError(message)
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!organisation) {
    return <div className="text-center text-gray-500 py-12">No organisation selected</div>
  }

  return (
    <div className="space-y-6">
      {/* Warning Banner */}
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
        <div>
          <h4 className="font-semibold text-red-800">Danger Zone</h4>
          <p className="text-sm text-red-600 mt-1">Actions in this section can have serious consequences. Please proceed with caution.</p>
        </div>
      </div>

      {/* Error Message */}
      {error && <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">{error}</div>}

      {/* Deactivate Organisation */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <Power className="h-5 w-5 text-orange-500" />
              <h4 className="font-semibold text-gray-800">{organisation.isActive ? 'Deactivate' : 'Activate'} Organisation</h4>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              {organisation.isActive
                ? 'Deactivating the organisation will prevent all users from signing in. Users will see an error message when they try to log in.'
                : 'Activating the organisation will allow users to sign in again.'}
            </p>
            <div className="mt-3">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${organisation.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                Current status: {organisation.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
          <Button variant={organisation.isActive ? 'destructive' : 'default'} onClick={handleDeactivate} disabled={submitting} className="ml-4">
            <Power className="h-4 w-4 mr-2" />
            {organisation.isActive ? 'Deactivate' : 'Activate'}
          </Button>
        </div>
      </div>

      {/* Delete Organisation */}
      <div className="bg-white border border-red-200 rounded-xl p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-500" />
              <h4 className="font-semibold text-gray-800">Delete Organisation</h4>
            </div>
            <p className="text-sm text-gray-500 mt-2">Permanently delete this organisation. This action cannot be undone.</p>
            <p className="text-sm text-red-600 mt-2">
              <strong>Note:</strong> You must remove all users and delete all subscriptions before deleting the organisation.
            </p>
          </div>
          <Button variant="destructive" onClick={handleDelete} disabled={submitting} className="ml-4">
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>
    </div>
  )
}

export default DangerTab
